'use client'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Radio,
  Users,
  DollarSign,
  Music,
  Play,
  StopCircle,
  ThumbsUp,
  MessageSquare,
  Coffee,
  Settings,
  Heart,
  Timer,
  Gift,
  QrCode,
  ExternalLink,
  PenLine,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import type { DemoEvent, DemoEventSettings } from '../demo-types'
import { AnimatedCounter, formatCompactNumber } from './animated-counter'

export interface StatsHeaderProps {
  event: DemoEvent
  settings: DemoEventSettings
  visitorCount: number
  queueLength: number
  reactionCount: number
  totalEarningsCents: number
  breakOnBreak: boolean
  notesCount: number
  activeTimerCount: number
  onEndBreak: () => void
  onOpenBreakDialog: () => void
  onOpenNotes: () => void
  onOpenTimerDialog: () => void
  onOpenQR: () => void
  onOpenSettings: () => void
  onOpenEndEvent: () => void
}

export function StatsHeader({
  event,
  settings,
  visitorCount,
  queueLength,
  reactionCount,
  totalEarningsCents,
  breakOnBreak,
  notesCount,
  activeTimerCount,
  onEndBreak,
  onOpenBreakDialog,
  onOpenNotes,
  onOpenTimerDialog,
  onOpenQR,
  onOpenSettings,
  onOpenEndEvent,
}: StatsHeaderProps) {
  const t = useTranslations('demo')
  return (
    <div className="space-y-3">
      {/* Event Title & Status */}
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-full bg-red-500/20 shrink-0">
          <Radio className="h-5 w-5 text-red-500 animate-pulse" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-white truncate">{event.title}</h1>
          {/* Status Tags */}
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">
              <span className="h-2 w-2 rounded-full bg-emerald-400 mr-1.5" />
              {t('connected')}
            </Badge>
            <Badge className={`text-xs ${settings.tipsEnabled ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30'}`}>
              <DollarSign className="h-3 w-3 mr-0.5" />
              {settings.tipsEnabled ? t('tips') : t('tipsOff')}
            </Badge>
            <Badge className={`text-xs ${settings.requestsEnabled ? 'bg-violet-500/20 text-violet-400 border-violet-500/30' : 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30'}`}>
              <Music className="h-3 w-3 mr-0.5" />
              {settings.requestsEnabled ? t('requests') : t('requestsOff')}
            </Badge>
            <Badge className={`text-xs ${settings.reactionsEnabled ? 'bg-pink-500/20 text-pink-400 border-pink-500/30' : 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30'}`}>
              <Heart className="h-3 w-3 mr-0.5" />
              {settings.reactionsEnabled ? t('reactions') : t('reactionsOff')}
            </Badge>
            <Badge className={`text-xs ${settings.feedbackEnabled ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' : 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30'}`}>
              <MessageSquare className="h-3 w-3 mr-0.5" />
              {settings.feedbackEnabled ? t('feedback') : t('feedbackOff')}
            </Badge>
          </div>
          <p className="text-sm text-zinc-400 mt-1">{event.venue || t('virtualEvent')}</p>
        </div>
      </div>

      {/* Event Control Buttons */}
      <div className="grid grid-cols-8 gap-1 w-full">
        {/* Break / I'm Back */}
        {breakOnBreak ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button onClick={onEndBreak} className="h-10 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 p-0">
                <Play className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="bg-zinc-800 border-zinc-700">{t('imBack')}</TooltipContent>
          </Tooltip>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" onClick={onOpenBreakDialog} className="h-10 border-amber-500/50 text-amber-400 hover:bg-amber-500/20 p-0">
                <Coffee className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="bg-zinc-800 border-zinc-700">{t('takeBreak')}</TooltipContent>
          </Tooltip>
        )}
        {/* Notes */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" onClick={onOpenNotes} className="h-10 border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20 relative p-0">
              <PenLine className="h-4 w-4" />
              {notesCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-cyan-500 text-black text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {notesCount > 9 ? '9+' : notesCount}
                </span>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent className="bg-zinc-800 border-zinc-700">{t('notes')}</TooltipContent>
        </Tooltip>
        {/* Timer */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" onClick={onOpenTimerDialog} className="h-10 border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20 relative p-0">
              <Timer className="h-4 w-4" />
              {activeTimerCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-emerald-500 text-black text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {activeTimerCount}
                </span>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent className="bg-zinc-800 border-zinc-700">{t('timers')}</TooltipContent>
        </Tooltip>
        {/* Promo (placeholder) */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" className="h-10 border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20 p-0">
              <Gift className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="bg-zinc-800 border-zinc-700">{t('promotions')}</TooltipContent>
        </Tooltip>
        {/* Public Page */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" className="h-10 border-zinc-600 text-zinc-300 hover:bg-zinc-700 p-0">
              <ExternalLink className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="bg-zinc-800 border-zinc-700">{t('viewPublicPage')}</TooltipContent>
        </Tooltip>
        {/* QR Code */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" onClick={onOpenQR} className="h-10 border-violet-500/50 text-violet-400 hover:bg-violet-500/20 p-0">
              <QrCode className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="bg-zinc-800 border-zinc-700">{t('showQRCode')}</TooltipContent>
        </Tooltip>
        {/* Settings */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" onClick={onOpenSettings} className="h-10 border-zinc-600 text-zinc-300 hover:bg-zinc-700 p-0" data-coach="adjust-settings">
              <Settings className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="bg-zinc-800 border-zinc-700">{t('settings')}</TooltipContent>
        </Tooltip>
        {/* End Event */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="destructive" onClick={onOpenEndEvent} className="h-10 hover:bg-red-500 p-0">
              <StopCircle className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="bg-zinc-800 border-zinc-700">{t('endEvent')}</TooltipContent>
        </Tooltip>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-2">
        <div className="aspect-square sm:aspect-auto sm:py-3 rounded-lg bg-blue-500/10 border border-blue-500/20 flex flex-col items-center justify-center p-1">
          <Users className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400 mb-0.5" />
          <span className="text-sm sm:text-lg font-bold text-white leading-none">{formatCompactNumber(visitorCount)}</span>
          <span className="text-[8px] sm:text-[10px] text-zinc-500 uppercase">{t('watchingStat')}</span>
        </div>
        <div className="aspect-square sm:aspect-auto sm:py-3 rounded-lg bg-violet-500/10 border border-violet-500/20 flex flex-col items-center justify-center p-1">
          <Music className="h-4 w-4 sm:h-5 sm:w-5 text-violet-400 mb-0.5" />
          <span className="text-sm sm:text-lg font-bold text-white leading-none">{formatCompactNumber(queueLength)}</span>
          <span className="text-[8px] sm:text-[10px] text-zinc-500 uppercase">{t('requestsStat')}</span>
        </div>
        <div className="aspect-square sm:aspect-auto sm:py-3 rounded-lg bg-pink-500/10 border border-pink-500/20 flex flex-col items-center justify-center p-1 relative">
          <ThumbsUp className="h-4 w-4 sm:h-5 sm:w-5 text-pink-400 mb-0.5" />
          <span className="text-sm sm:text-lg font-bold text-white leading-none">{formatCompactNumber(reactionCount)}</span>
          <span className="text-[8px] sm:text-[10px] text-zinc-500 uppercase">{t('reactionsStat')}</span>
        </div>
        <div data-coach="artist-tips-stat" className="aspect-square sm:aspect-auto sm:py-3 rounded-lg bg-green-500/10 border border-green-500/20 flex flex-col items-center justify-center p-1">
          <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-green-400 mb-0.5" />
          <AnimatedCounter value={totalEarningsCents} prefix="$" className="text-sm sm:text-lg font-bold text-white leading-none" />
          <span className="text-[8px] sm:text-[10px] text-zinc-500 uppercase">{t('tipsStat')}</span>
        </div>
      </div>
    </div>
  )
}
